import React, { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { logger } from "@/lib/logger";
import usePaymentPolling from "@/hooks/usePaymentPolling";

const formatKes = (v) => `KES ${Number(v || 0).toLocaleString("en-KE")}`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-KE", {
      timeZone: "Africa/Nairobi",
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function PaystackReceipt({ order }) {
  return (
    <div className="w-full max-w-md mx-auto text-left" data-testid="paystack-receipt">
      <div className="overline text-mizizi-terracotta text-center">Asante sana</div>
      <h1 className="font-serif font-light text-5xl md:text-6xl mt-4 leading-none text-mizizi-charcoal text-center">
        Payment confirmed.
      </h1>
      <p className="mt-4 text-mizizi-charcoal/70 text-center">
        A signed download link and magic-link login are on the way to your inbox.
      </p>

      {/* Receipt card — ticket cut edges */}
      <div className="relative mt-10 bg-mizizi-cream border border-mizizi-charcoal/15 shadow-2xl print:shadow-none">
        <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-mizizi-cream border border-mizizi-charcoal/15" />
        <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-mizizi-cream border border-mizizi-charcoal/15" />

        <div className="p-6 md:p-8">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="overline text-mizizi-terracotta">Paystack Receipt</div>
              <div className="font-serif text-2xl mt-1 leading-tight">{order.product_name || "Mizizi"}</div>
            </div>
            <div className="font-serif text-2xl text-mizizi-gold" data-testid="receipt-amount">
              {formatKes(order.amount_kes)}
            </div>
          </div>

          <div className="my-6 relative">
            <div className="border-t border-dashed border-mizizi-charcoal/20" />
          </div>

          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="overline text-mizizi-charcoal/50">Channel</dt>
              <dd className="text-mizizi-charcoal font-semibold" data-testid="receipt-channel">
                {order.paystack_channel || "—"}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="overline text-mizizi-charcoal/50">Reference</dt>
              <dd className="font-mono tracking-wider text-mizizi-charcoal text-xs break-all" data-testid="receipt-reference">
                {order.paystack_receipt || order.reference || "—"}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="overline text-mizizi-charcoal/50">Paid by</dt>
              <dd className="text-mizizi-charcoal">{order.fullname || "—"}</dd>
            </div>
            {order.phone_masked && (
              <div className="flex justify-between gap-4">
                <dt className="overline text-mizizi-charcoal/50">Phone</dt>
                <dd className="text-mizizi-charcoal font-mono" data-testid="receipt-phone">{order.phone_masked}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="overline text-mizizi-charcoal/50">Date</dt>
              <dd className="text-mizizi-charcoal">{formatDate(order.paid_at)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="overline text-mizizi-charcoal/50">Order</dt>
              <dd className="text-mizizi-charcoal font-mono text-xs break-all">{order.order_id}</dd>
            </div>
          </dl>

          <div className="my-6 relative">
            <div className="border-t border-dashed border-mizizi-charcoal/20" />
          </div>

          <div className="text-center">
            <div className="overline text-mizizi-gold">Verified · Paystack</div>
            <div className="text-[10px] text-mizizi-charcoal/50 mt-1 tracking-widest uppercase">
              Save this reference · For support
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3 justify-center print:hidden">
        <Link to="/login" data-testid="success-login" className="pill pill-primary">Sign in to the Reader</Link>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="receipt-print"
          className="pill pill-outline"
        >
          Print receipt
        </button>
      </div>
    </div>
  );
}

function StripeSuccess() {
  return (
    <>
      <div className="overline text-mizizi-terracotta">Thank you</div>
      <h1 className="font-serif font-light text-5xl md:text-6xl mt-6 leading-none">The table is yours.</h1>
      <p className="mt-6 text-mizizi-charcoal/70">A signed download link and your magic-link login will arrive by email. Sign in to unlock the reader.</p>
      <Link to="/login" data-testid="success-login" className="pill pill-primary mt-8 inline-flex">Sign In to the Reader</Link>
    </>
  );
}

function TimeoutView() {
  return (
    <>
      <div className="overline text-mizizi-terracotta">One moment</div>
      <h1 className="font-serif font-light text-4xl mt-4">Verifying your payment…</h1>
      <p className="mt-4 text-mizizi-charcoal/70">If this persists, contact hello@mizizitable.com with your order ID.</p>
    </>
  );
}

function PollingView() {
  return (
    <>
      <div className="overline text-mizizi-terracotta">Working</div>
      <h1 className="font-serif font-light text-4xl mt-4">Confirming your seat…</h1>
      <div className="mt-8 flex justify-center">
        <div className="w-3 h-3 rounded-full bg-mizizi-gold animate-pulse" />
      </div>
    </>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const reference = params.get("reference");
  const isMock = params.get("mock") === "1";

  const inferProvider = () => {
    if (sessionId) return "stripe";
    if (reference) return "paystack";
    return "stripe";
  };
  const provider = params.get("provider") || inferProvider();

  // Sandbox: auto-fulfill the order once so preview end-to-end works without live keys.
  const mockCompletedRef = useRef(false);
  useEffect(() => {
    if (!isMock || provider !== "paystack" || !reference || mockCompletedRef.current) return;
    mockCompletedRef.current = true;
    client.post(`/payments/paystack/mock-complete/${reference}`).catch((err) => {
      logger.error("Paystack mock-complete failed:", err);
    });
  }, [isMock, provider, reference]);

  const url = provider === "paystack"
    ? (reference && `/payments/paystack/status/${reference}`)
    : (sessionId && `/payments/status/${sessionId}`);
  const { status, order } = usePaymentPolling(url, { enabled: !!url });

  let content;
  if (status === "paid" && provider === "paystack" && order) {
    content = <PaystackReceipt order={order} />;
  } else if (status === "paid") {
    content = <StripeSuccess />;
  } else if (status === "timeout" || status === "error") {
    content = <TimeoutView />;
  } else {
    content = <PollingView />;
  }

  return (
    <div className="min-h-[80vh] bg-mizizi-cream flex items-center justify-center px-6 py-24 text-center print:py-8">
      <div className="max-w-2xl w-full">{content}</div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[80vh] bg-mizizi-cream flex items-center justify-center px-6 py-24 text-center">
      <div className="max-w-md">
        <div className="overline text-mizizi-terracotta">No harm done</div>
        <h1 className="font-serif font-light text-5xl mt-6">Come back when you're ready.</h1>
        <p className="mt-6 text-mizizi-charcoal/70">Nothing was charged. The table is always set.</p>
        <Link to="/shop" data-testid="cancel-shop" className="pill pill-primary mt-8 inline-flex">Return to Shop</Link>
      </div>
    </div>
  );
}
